/**
 * Compact date formatting utilities for the celebration app
 * Following the principle: show only what's needed at each level
 * - Celebration: full dates (start - end)
 * - Event: day/month + time
 * - Activity: day + time only
 */

/**
 * Returns the current locale for date formatting.
 * Defaults to the browser's language, falling back to 'en-US'.
 * Can be overridden in the future via user preferences or Zustand store.
 */
export function getLocale(): string {
  return navigator?.language || 'en-US';
}

/**
 * Format celebration dates (full date range)
 * e.g., "Dec 15 - 20, 2026" or "Dec 15, 2026"
 */
export function formatCelebrationDates(startDate: string, endDate?: string): string {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : null;
  
  const startMonth = start.toLocaleDateString(getLocale(), { month: 'short' });
  const startDay = start.getDate();
  const startYear = start.getFullYear();
  
  if (!end || start.toDateString() === end.toDateString()) {
    return `${startMonth} ${startDay}, ${startYear}`;
  }
  
  const endMonth = end.toLocaleDateString(getLocale(), { month: 'short' });
  const endDay = end.getDate();
  const endYear = end.getFullYear();
  
  // Same month and year
  if (startMonth === endMonth && startYear === endYear) {
    return `${startMonth} ${startDay}-${endDay}, ${startYear}`;
  }
  
  // Same year
  if (startYear === endYear) {
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${startYear}`;
  }
  
  // Different years
  return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`;
}

/**
 * Format event date/time (day/month + time)
 * e.g., "15/12 · 2:00 PM" or "Sat 15/12 · 2:00 PM"
 */
export function formatEventDateTime(dateString: string, includeWeekday = false): string {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const time = date.toLocaleTimeString(getLocale(), { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
  
  if (includeWeekday) {
    const weekday = date.toLocaleDateString(getLocale(), { weekday: 'short' });
    return `${weekday} ${day}/${month} · ${time}`;
  }
  
  return `${day}/${month} · ${time}`;
}

/**
 * Format event date range (for multi-day events within celebration)
 * e.g., "15/12 2PM - 18/12 6PM"
 */
export function formatEventDateRange(startDate: string, endDate?: string): string {
  const start = new Date(startDate);
  const startStr = formatEventDateTime(startDate);
  
  if (!endDate) return startStr;
  
  const end = new Date(endDate);
  
  // Same day - just show time range
  if (start.toDateString() === end.toDateString()) {
    const startTime = start.toLocaleTimeString(getLocale(), { hour: 'numeric', minute: '2-digit', hour12: true });
    const endTime = end.toLocaleTimeString(getLocale(), { hour: 'numeric', minute: '2-digit', hour12: true });
    const day = start.getDate().toString().padStart(2, '0');
    const month = (start.getMonth() + 1).toString().padStart(2, '0');
    return `${day}/${month} · ${startTime} - ${endTime}`;
  }
  
  // Different days
  const endStr = formatEventDateTime(endDate);
  return `${startStr} → ${endStr}`;
}

/**
 * Format activity time (day + time only, since it's within an event)
 * e.g., "Sat · 2:00 PM" or "2:00 PM" (if same day as event)
 */
export function formatActivityTime(dateString: string, eventDate?: string): string {
  const date = new Date(dateString);
  const time = date.toLocaleTimeString(getLocale(), { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
  
  // If event date provided and same day, just show time
  if (eventDate) {
    const event = new Date(eventDate);
    if (date.toDateString() === event.toDateString()) {
      return time;
    }
  }
  
  // Show weekday + time
  const weekday = date.toLocaleDateString(getLocale(), { weekday: 'short' });
  return `${weekday} · ${time}`;
}

/**
 * Format activity time range
 * e.g., "2:00 - 3:30 PM" or "2:00 PM - 3:30 PM"
 */
export function formatActivityTimeRange(startTime?: string, endTime?: string): string {
  if (!startTime) return 'Time TBD';
  
  const start = new Date(startTime);
  const startStr = start.toLocaleTimeString(getLocale(), { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
  
  if (!endTime) return startStr;
  
  const end = new Date(endTime);
  const endStr = end.toLocaleTimeString(getLocale(), { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
  
  // Compact format: remove duplicate AM/PM if same
  const startPeriod = start.getHours() >= 12 ? 'PM' : 'AM';
  const endPeriod = end.getHours() >= 12 ? 'PM' : 'AM';
  
  if (startPeriod === endPeriod) {
    const startNoAmPm = start.toLocaleTimeString(getLocale(), { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    }).replace(/ (AM|PM)/, '');
    return `${startNoAmPm} - ${endStr}`;
  }
  
  return `${startStr} - ${endStr}`;
}

/**
 * Get relative date label
 * e.g., "Today", "Tomorrow", "In 3 days", "Dec 15"
 */
export function getRelativeDateLabel(dateString: string): string {
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  
  const diffDays = Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays > 0 && diffDays <= 7) return `In ${diffDays} days`;
  if (diffDays < 0 && diffDays >= -7) return `${Math.abs(diffDays)} days ago`;
  
  return date.toLocaleDateString(getLocale(), { month: 'short', day: 'numeric' });
}

/**
 * Format countdown
 * e.g., "3d 5h", "5h 30m", "30m"
 */
export function formatCountdown(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diff = date.getTime() - now.getTime();
  
  if (diff <= 0) return 'Now';
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/**
 * Format short date for compact displays
 * e.g., "Dec 15" or "15/12"
 */
export function formatShortDate(dateString: string, useSlash = false): string {
  const date = new Date(dateString);
  
  if (useSlash) {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${day}/${month}`;
  }
  
  return date.toLocaleDateString(getLocale(), { month: 'short', day: 'numeric' });
}
